import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, Modal, StyleSheet, Platform, ActivityIndicator, ScrollView } from 'react-native';
import { MdClose } from 'react-icons/md';
import CountryCitySelector from './CountryCitySelector';

interface ItinerarySettingsModalProps {
  visible: boolean;
  onClose: () => void;
  initialData: {
    title: string;
    coverUrl?: string;
    startDate?: string;
    currency?: string;
    location?: string;
  };
  onSave: (data: {
    title: string;
    coverUrl?: string;
    startDate?: string;
    currency?: string;
    location?: string;
  }) => Promise<void>;
}

const CURRENCIES = [
  { code: 'CNY', label: 'CNY - 人民幣' },
  { code: 'TWD', label: 'TWD - 新台幣' },
  { code: 'HKD', label: 'HKD - 港幣' },
  { code: 'USD', label: 'USD - 美元' },
  { code: 'JPY', label: 'JPY - 日圓' },
  { code: 'KRW', label: 'KRW - 韓元' },
  { code: 'EUR', label: 'EUR - 歐元' },
  { code: 'GBP', label: 'GBP - 英鎊' },
];

const ItinerarySettingsModal: React.FC<ItinerarySettingsModalProps> = ({
  visible,
  onClose,
  initialData,
  onSave,
}) => {
  const [title, setTitle] = useState(initialData.title);
  const [coverUrl, setCoverUrl] = useState(initialData.coverUrl || '');
  const [startDate, setStartDate] = useState(initialData.startDate || '');
  const [currency, setCurrency] = useState(initialData.currency || 'CNY');
  const [location, setLocation] = useState(initialData.location || '');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setTitle(initialData.title || '');
      setCoverUrl(initialData.coverUrl || '');
      setStartDate(initialData.startDate || '');
      setCurrency(initialData.currency || 'CNY');
      setLocation(initialData.location || '');
    }
  }, [visible, initialData]);

  const handleSave = async () => {
    if (!title.trim()) {
      alert('請輸入行程標題');
      return;
    }
    setLoading(true);
    try {
      await onSave({
        title,
        coverUrl,
        startDate: startDate || undefined,
        currency,
        location,
      });
      onClose();
    } catch (e) {
      console.error(e);
      alert('保存失敗，請重試');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.dialog}>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>行程設置</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <MdClose size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            <View style={styles.field}>
              <Text style={styles.label}>行程標題</Text>
              <TextInput
                style={styles.input}
                value={title}
                onChangeText={setTitle}
                placeholder="輸入行程標題"
                editable={!loading}
              />
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>出發日期</Text>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: loading ? '#f5f5f5' : '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
                disabled={loading}
              />
              <Text style={styles.hint}>修改日期將自動重新計算所有行程天數的日期</Text>
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>目的地城市</Text>
              <CountryCitySelector
                value={location}
                onChange={setLocation}
              />
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>預算貨幣</Text>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  fontSize: '16px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  backgroundColor: loading ? '#f5f5f5' : '#fff',
                  outline: 'none',
                  boxSizing: 'border-box',
                  fontFamily: 'inherit'
                }}
                disabled={loading}
              >
                {CURRENCIES.map(c => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
            </View>
          </ScrollView>

          <View style={styles.footer}>
            <TouchableOpacity 
              style={[styles.button, styles.cancelButton]} 
              onPress={onClose}
              disabled={loading}
            >
              <Text style={styles.cancelText}>取消</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.button, styles.confirmButton, loading && styles.disabledButton]} 
              onPress={handleSave}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={styles.confirmText}>保存設置</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialog: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#fff',
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90%',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
      },
      default: {
        elevation: 5,
      },
    }),
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#111',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    padding: 24,
  },
  field: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#444',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  hint: {
    fontSize: 12,
    color: '#999',
    marginTop: 6,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#f5f5f5',
  },
  confirmButton: {
    backgroundColor: '#FB7A4A',
  },
  disabledButton: {
    opacity: 0.7,
  },
  cancelText: {
    color: '#666',
    fontWeight: '600',
    fontSize: 16,
  },
  confirmText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 16,
  },
});

export default ItinerarySettingsModal;
